import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import ReactSelect, { components } from 'react-select';
import flatMap from 'lodash/flatMap';
import hooks from '@g4/automation-hooks';

import { VisuallyHidden } from '../../Boneyard';

import toStyleObject from '../../../utils/toStyleObject';
import { conditionallyRequired } from '../../../utils/propTypesUtils';

export const propTypes = {
  /** Input field `name` attribute value */
  name: PropTypes.string.isRequired,
  /** Collection of options */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      statusLabel: PropTypes.string,
    })
  ).isRequired,
  /** The selected value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Globally unique ID used internally for creating a relationship between the input field and label */
  id: conditionallyRequired(PropTypes.string, props => props.label),
  /**
   * Indicates that the field is required
   *
   * Depends on a `label` value
   */
  isRequired: PropTypes.bool,
  /** Text to display whilst a value hasn't been selected */
  placeholder: PropTypes.string,
  /** Display the drop down indicator */
  withDropDownIndicator: PropTypes.bool,
  /** Display a border around the input */
  withBorder: PropTypes.bool,
  /** Allow the user to search for matching options */
  isSearchable: PropTypes.bool,
  /** Prevents options from being selected */
  disabled: PropTypes.bool,
  /** Applies error styling */
  hasError: PropTypes.bool,
  /** Formats group labels in the menu as React components */
  formatGroupLabel: PropTypes.func,
  /** Text to display when there are no options */
  noOptionsMessage: PropTypes.func,
  /** onchange callback handler */
  onChange: PropTypes.func,
  /** onMenuOpen callback handler */
  onMenuOpen: PropTypes.func,
  /** onMenuClose callback handler */
  onMenuClose: PropTypes.func,
  /** onblur callback handler */
  onBlur: PropTypes.func,
  menuPortalTarget: PropTypes.object,
  menuShouldBlockScroll: PropTypes.bool,
  menuPlacement: PropTypes.string,
  /**
   * Associates a specific action onto the input field for the benefit of automated clients consuming it
   * Also used as a prefix for each select option
   */
  errorId: PropTypes.string,
  menuPortalStyles: PropTypes.object,
  automationHook: PropTypes.string,
  theme: PropTypes.object,
  autoComplete: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export const defaultProps = {
  placeholder: 'Select',
  withDropDownIndicator: true,
  withBorder: true,
  isSearchable: false,
  onChange: Function.prototype,
  onBlur: Function.prototype,
  onMenuOpen: Function.prototype,
  onMenuClose: Function.prototype,
  hasError: false,
  menuPortalStyles: {},
};

const downArrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="27"
    height="27"
    viewBox="0 0 27 27"
  >
    <g fill="none" fillRule="evenodd">
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M8 11l7 8 7-8"
      />
    </g>
  </svg>
);

export const GroupDivider = styled.hr`
  border: none;
  border-bottom: 1px solid ${props => props.theme.colors.select.groupDivider};
  margin: ${props => `${props.theme.spacing(2)} ${props.theme.spacing(4)}`};
`;

export const Asterisk = styled.span.attrs({
  children: <React.Fragment>&lowast;</React.Fragment>,
})`
  position: relative;
  top: -3px;
  color: ${props => props.theme.colors.input.required};
  margin-left: ${props => props.theme.spacing(1)};
`;

const CustomGroupHeading = props => (
  <React.Fragment>
    <VisuallyHidden>
      <components.GroupHeading {...props} />
    </VisuallyHidden>
    <GroupDivider />
  </React.Fragment>
);

/** @visibleName Select */
const Select = ({
  name,
  options,
  id,
  isRequired,
  placeholder,
  value,
  theme,
  disabled,
  hasError,
  isSearchable,
  withDropDownIndicator,
  withBorder,
  menuPortalTarget,
  formatGroupLabel,
  noOptionsMessage,
  onChange,
  onMenuOpen,
  onMenuClose,
  onBlur,
  automationHook,
  menuPortalStyles,
  errorId,
  menuShouldBlockScroll,
  menuPlacement,
  autoComplete,
  ariaLabel,
}) => {
  const inputRef = React.useRef();

  const getCurrentValue = () => {
    const flatOptions = flatMap(options, item =>
      item.options ? item.options : item
    );
    return (
      flatOptions.find(
        option => typeof option.value !== 'undefined' && option.value === value
      ) || null
    );
  };

  const CustomControl = React.useCallback(
    props => (
      <div {...hooks.hook(automationHook)}>
        <components.Control {...props} />
      </div>
    ),
    [automationHook]
  );

  const OptionComponent = props => (
    <div
      {...hooks.hook(
        automationHook,
        props.data.value ? props.data.value : 'none'
      )}
    >
      <components.Option {...props}>
        {props.data.label} {props.data.statusLabel}
      </components.Option>
    </div>
  );

  OptionComponent.propTypes = {
    data: PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node,
      statusLabel: PropTypes.string,
    }),
  };

  const CustomOption = React.useCallback(OptionComponent, [automationHook]);

  const onChangeCallback = React.useCallback(
    selection => {
      inputRef.current.value = selection.value;
      onChange(selection.value);
    },
    [onChange]
  );

  const getReactSelectTheme = React.useCallback(
    reactSelectTheme => ({
      ...reactSelectTheme,
      borderRadius: 8,
      ...theme.colors.select.reactSelectTheme,
    }),
    [theme]
  );

  const reactSelectComponents = React.useMemo(
    () => ({
      DropdownIndicator: withDropDownIndicator ? downArrow : null,
      IndicatorSeparator: null,
      Option: CustomOption,
      Control: CustomControl,
      GroupHeading: CustomGroupHeading,
    }),
    [CustomControl, CustomOption, withDropDownIndicator]
  );

  const reactSelectStyles = React.useMemo(
    () => ({
      container: provided => {
        return {
          ...provided,
          width: '100%',
        };
      },
      control: (provided, { isFocused }) => {
        const focusStyle = `
                outline: none;
                box-shadow: 0 0 0 2px ${theme.colors.input.focused};
              `;

        let styles = {
          ...provided,
          minHeight: '50px',
          height: '50px',
          padding: theme.spacing(2),
          background: theme.colors.select.background,
          cursor: 'pointer',

          '&:hover': {
            ...toStyleObject(focusStyle),
          },
        };

        styles.border = withBorder
          ? `1px solid ${theme.colors.select.border}`
          : 'none';

        if (isFocused) {
          styles = {
            ...styles,
            ...toStyleObject(focusStyle),
          };
        }

        if (disabled) {
          styles.color = theme.colors.select.disabledText;
          styles.background = theme.colors.select.disabledBackground;
        }

        if (hasError && !disabled) {
          styles.borderColor = `${theme.colors.common.alertRed} !important`;
        }

        return styles;
      },
      placeholder: provided => ({
        ...provided,
        whiteSpace: 'nowrap',
        color: theme.colors.select.placeholderText,
      }),
      menuPortal: provided => ({
        ...provided,
        ...menuPortalStyles,
      }),
      menu: provided => ({
        ...provided,
        backgroundColor: theme.colors.select.background,
        zIndex: 2,
      }),
      group: provided => ({
        ...provided,
        padding: 0,
        [`&:first-of-type ${GroupDivider}`]: {
          display: 'none',
        },
      }),
      noOptionsMessage: provided => ({
        ...provided,
        textAlign: 'left',
        color: theme.colors.select.optionText,
        fontSize: theme.toRem(14),
      }),
      option: (provided, { isDisabled, isFocused, isSelected }) => {
        let styles = {
          ...provided,
          paddingLeft: theme.spacing(4),
          paddingRight: theme.spacing(4),
          backgroundColor: theme.colors.select.option,
          color: theme.colors.select.optionText,
          fontSize: theme.toRem(14),
          zIndex: 10,
          cursor: 'pointer',
        };

        if (!isDisabled) {
          styles.cursor = 'pointer';

          if (isSelected || isFocused) {
            styles.backgroundColor = theme.colors.select.optionSelected;
          }
        } else {
          styles.color = theme.colors.select.disabledText;
          styles.cursor = 'normal';
        }

        return styles;
      },
    }),
    [disabled, hasError, menuPortalStyles, theme, withBorder]
  );

  const isReactSelectOptionDisabled = React.useCallback(
    option => option.disabled,
    []
  );

  return (
    <div
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ReactSelect
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        aria-invalid={hasError}
        aria-labelledby={hasError ? errorId : undefined}
        menuShouldBlockScroll={menuShouldBlockScroll}
        menuPlacement={menuPlacement}
        menuPortalTarget={menuPortalTarget || document.querySelector('body')}
        inputId={id}
        value={getCurrentValue()}
        isDisabled={disabled}
        isOptionDisabled={isReactSelectOptionDisabled}
        instanceId={name}
        options={options}
        noOptionsMessage={noOptionsMessage}
        onChange={onChangeCallback}
        tabSelectsValue={false}
        onBlur={onBlur}
        isSearchable={isSearchable}
        blurInputOnSelect={false}
        formatGroupLabel={formatGroupLabel}
        placeholder={placeholder}
        theme={getReactSelectTheme}
        components={reactSelectComponents}
        styles={reactSelectStyles}
        aria-label={ariaLabel ? ariaLabel : name}
      />
      <input
        ref={inputRef}
        name={name}
        type="hidden"
        {...hooks.hook(automationHook, 'input')}
        autoComplete={autoComplete}
        aria-label={ariaLabel ? ariaLabel : name}
      />
    </div>
  );
};

// this is to differentiate between our Select and the one which is
// defined by react-select
Select.displayName = 'BoneyardSelect';
Select.propTypes = propTypes;
Select.defaultProps = defaultProps;

/** @component */
export default withTheme(Select);
